import React, { useMemo, useState, useEffect, useRef } from 'react';
import { differenceInDays } from 'date-fns';
import { faEllipsisVertical, faTrash, faUser, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Table, { getDensityClass } from '@frontend/Components/Table/Table';
import type { Column } from '@frontend/Models/Table/TableModels';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import type { Stakeholder } from '../types';
import type { SourceTabType, TabType } from '../types';
import { getInitials } from '../utils';

interface OrbitStakeholdersTableProps {
  data: Stakeholder[];
  activeSourceTab: SourceTabType;
  activeTypeTab: TabType;
  emptyMessage: string;
  onStakeholderClick: (stakeholder: Stakeholder) => void;
  onOrganizationClick: (organizationName: string, e: React.MouseEvent) => void;
  onDeleteStakeholder?: (stakeholder: Stakeholder) => void;
  pagination: {
    currPage: number;
    onPageChange: (page: number) => void;
    pageLimit: number;
    pageSize: number;
    onPageSizeChange: (size: number) => void;
    totalItems: number;
    itemLabel: string;
  };
}

const OrbitStakeholdersTable: React.FC<OrbitStakeholdersTableProps> = ({
  data,
  activeSourceTab,
  activeTypeTab,
  emptyMessage,
  onStakeholderClick,
  onOrganizationClick,
  onDeleteStakeholder,
  pagination,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const menuRef = menuRefs.current[openMenuId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const toggleMenu = (stakeholderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === stakeholderId ? null : stakeholderId);
  };

  const handleDeleteClick = (stakeholder: Stakeholder, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (onDeleteStakeholder) {
      onDeleteStakeholder(stakeholder);
    }
  };

  const columns = useMemo((): Column<Stakeholder>[] => {
    const baseColumns: Column<Stakeholder>[] = [];
    const isIndividual = activeTypeTab === 'individuals';
    const firstColumnIcon = isIndividual ? faUser : faBuilding;
    const iconColor = '#6f42c1';
    const showSourceBadge = activeSourceTab === 'all';

    baseColumns.push({
      header: isIndividual ? "Stakeholder's Name" : 'Organization Name',
      render: (row) => (
        <div
          className="matter-name-cell stakeholder-name-clickable"
          onClick={() => onStakeholderClick(row)}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onStakeholderClick(row);
            }
          }}
        >
          <div
            className="matter-icon-wrapper"
            style={{ backgroundColor: iconColor }}
          >
            <FontAwesomeIcon icon={firstColumnIcon} className="matter-type-icon" />
          </div>
          <div className="matter-name-content">
            <div className="matter-name-text">
              {row.name || '—'}
              {row.type === 'organization' && row.legacy && (
                <span
                  className="stakeholder-source-badge stakeholder-source-badge-other"
                  style={{ marginLeft: 6 }}
                  title="Grouped by its company text only — not yet linked to an organization record"
                >
                  not yet linked
                </span>
              )}
            </div>
            <div className="matter-id-text">
              {row.id || '—'}
              {showSourceBadge && (
                <span
                  className={`stakeholder-source-badge stakeholder-source-badge-${row.source === 'client' ? 'client' : 'other'}`}
                  title={row.source === 'client' ? 'From Clients table' : 'From Other (Agents) table'}
                >
                  {row.source === 'client' ? 'Client' : 'Other'}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    });

    baseColumns.push(
      {
        header: 'Title',
        render: (row) => (
          <div className="stakeholder-role">{row.roleTitle || '-'}</div>
        ),
      },
      {
        header: 'Jurisdiction',
        render: (row) => (
          <div className="stakeholder-role">{row.jurisdiction || '-'}</div>
        ),
      },
      {
        header: 'Primary Contact',
        render: (row) => {
          const contactName = row.primaryContact?.name || row.name || '-';
          const initials = contactName !== '-' ? getInitials(contactName) : '-';
          const title = row.roleTitle || '';
          return (
            <div className="stakeholder-contact-cell">
              <div className="stakeholder-avatar">
                {initials !== '-' ? initials : '-'}
              </div>
              <div className="stakeholder-contact-info">
                <div className="stakeholder-contact-name">{contactName}</div>
                {title && <div className="stakeholder-contact-title">{title}</div>}
              </div>
            </div>
          );
        },
      },
    );

    if (activeTypeTab === 'individuals') {
      baseColumns.push({
        header: 'Organization',
        render: (row) =>
          row.organization ? (
            <span
              className="stakeholder-org-badge stakeholder-org-clickable"
              onClick={(e) => onOrganizationClick(row.organization!, e)}
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOrganizationClick(row.organization!, e as unknown as React.MouseEvent);
                }
              }}
            >
              {row.organization}
            </span>
          ) : (
            <span className="stakeholder-org-badge empty">-</span>
          ),
      });
    }

    baseColumns.push(
      {
        header: 'Assigned Lead',
        render: (row) =>
          row.assignedLead ? (
            <span className="stakeholder-lead-badge">{row.assignedLead}</span>
          ) : (
            <span className="stakeholder-lead-badge empty">-</span>
          ),
      },
      {
        header: 'Last Active',
        render: (row) => {
          if (!row.lastActive) return <span>-</span>;
          const now = new Date();
          const daysUntil = differenceInDays(row.lastActive, now);
          const dateStr = row.lastActive.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
          return (
            <div className="stakeholder-last-active">
              <div className="stakeholder-last-active-date">{dateStr}</div>
              {daysUntil !== 0 && (
                <div className="stakeholder-days-left">
                  {Math.abs(daysUntil)} {daysUntil > 0 ? 'days left' : 'days ago'}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: isEngineeringEnvironment ? 'Projects' : 'Matters',
        render: (row) => (
          <span className="stakeholder-matters-badge">
            {row.mattersCount || 0}
          </span>
        ),
      }
    );

    if (onDeleteStakeholder) {
      baseColumns.push({
        header: (
          <div className="table-action-header">
            <span className="table-action-header-text">Actions</span>
          </div>
        ),
        render: (row) => {
          const isMenuOpen = openMenuId === row.id;

          return (
            <div
              className="table-action-cell"
              ref={(el) => {
                if (el) menuRefs.current[row.id] = el;
              }}
            >
              <button
                className="table-action-button"
                aria-label={`Actions for ${row.name}`}
                aria-expanded={isMenuOpen}
                onClick={(e) => toggleMenu(row.id, e)}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>
              {isMenuOpen && (
                <div className="table-action-dropdown">
                  <button
                    className="table-action-dropdown-item table-action-dropdown-item-danger"
                    onClick={(e) => handleDeleteClick(row, e)}
                    aria-label="Delete client"
                  >
                    <FontAwesomeIcon icon={faTrash} className="table-action-dropdown-icon" />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [activeSourceTab, activeTypeTab, onStakeholderClick, onOrganizationClick, onDeleteStakeholder, openMenuId]);

  return (
    <div className={getDensityClass(pagination?.pageSize ?? 7)}>
      <Table
        columns={columns}
        data={data}
        emptyMessage={emptyMessage}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
};

export default OrbitStakeholdersTable;
